// /quotes/:id/checkout — the customer's payment surface. Lands here
// after Submit returns `kind: 'submitted'` with `requires_payment: true`.
//
// Flow:
//   1. Load the quote (must be status='submitted')
//   2. POST /quotes/:id/checkout to provision a Payment + provider
//      transaction
//   3. Render PaymentForm → ProviderCardField
//   4. After provider confirms, poll /quotes/:id until status='paid'
//      (in v1 with the mock provider, /api/dev/mock-payment-success
//      makes step 4 effectively instant)

import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { resolveDeliveryMode } from 'shared';

import { extractApiError } from '../api/client.js';
import * as quotesApi from '../api/quotes.js';
import * as submissionApi from '../api/submission.js';
import { ANALYTICS_EVENTS, trackEvent } from '../lib/analytics/index.js';
import { PRIVACY_POLICY_URL, TERMS_URL } from '../lib/legalLinks.js';
import { useIsImpersonating } from '../auth/AuthContext.js';
import { OrderLineItems } from '../components/OrderLineItems.js';
import { PaymentForm } from '../components/payment/PaymentForm.js';
import { formatCurrency } from '../i18n/formatters.js';
import { quoteStatusLabel } from '../i18n/quoteStatus.js';
import { ARTWORK_PENDING_DISCLAIMER } from '../pricingCopy.js';
import { lookupShippingRate } from '../shipping/estimate.js';

// Mirrors the backend's PAYABLE_STATUSES (payments/service.ts). A quote
// is payable once it has cleared its approval gate — the self-service
// 'submitted' path, or the Nexus-approved 'approved' path for a quote a
// sales rep took over (control='sales').
const PAYABLE_QUOTE_STATUSES = new Set(['submitted', 'approved']);

export function Checkout(): JSX.Element {
  const { id } = useParams();
  const { t } = useTranslation(['checkout', 'common']);
  const impersonating = useIsImpersonating();
  const [checkout, setCheckout] = useState<submissionApi.CheckoutResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  // Customer must review the order + accept the terms before the payment
  // form is shown (a normal checkout gate, not the doc_service signature).
  const [accepted, setAccepted] = useState(false);
  // Defensive: if confirmation hasn't landed shortly after a charge, show a
  // reassuring note instead of an indefinitely "Processing…" button.
  const [confirmingSlow, setConfirmingSlow] = useState(false);
  // "Download order PDF" on the paid view reads a short-lived URL from the
  // backend (which proxies doc_service); null = not ready yet.
  const [downloadMsg, setDownloadMsg] = useState<string | null>(null);

  const quoteQuery = useQuery({
    queryKey: ['quote', id],
    queryFn: () => quotesApi.getQuote(id!),
    // Skipped during impersonation — payment is blocked then (below).
    enabled: Boolean(id) && !impersonating,
    // Poll while we're waiting on payment confirmation. Stops once
    // status flips to 'paid'.
    refetchInterval: (query) =>
      paymentInitiated && query.state.data?.status !== 'paid' ? 1500 : false,
  });

  // `checkout` is only set AFTER the await returns, so it cannot guard the window
  // the request is open in — and `quoteQuery.data` is a dependency, so any
  // refetch landing inside that window (TanStack refetches on window focus by
  // default; StrictMode double-invokes in dev) re-enters and provisions a SECOND
  // provider session. A ref, not state: it must be true before the next render,
  // not after it.
  const checkoutStarted = useRef(false);

  useEffect(() => {
    if (impersonating) return;
    if (!id || checkout || checkoutStarted.current || !quoteQuery.data) return;
    // Payable states: 'submitted' (self-service) or 'approved' (a
    // Nexus-owned quote Nexus has approved — see services/quotes/status.ts).
    if (!PAYABLE_QUOTE_STATUSES.has(quoteQuery.data.status)) return;
    checkoutStarted.current = true;
    void (async () => {
      try {
        const result = await submissionApi.startCheckout(id);
        // The provider session exists and the card form is about to render —
        // the denominator for "started paying but did not finish".
        trackEvent(ANALYTICS_EVENTS.payment_started, { surface: 'self_serve' });
        setCheckout(result);
      } catch (err) {
        // Released on failure so a transient error is retryable — the guard is
        // against a concurrent second session, not against ever trying again.
        checkoutStarted.current = false;
        setErrorMessage(extractApiError(err)?.message ?? t('page.error.startCheckoutFailed'));
      }
    })();
  }, [id, checkout, quoteQuery.data, impersonating, t]);

  // No-silent-hang guard: once a charge is initiated, give confirmation a
  // few seconds; if the quote still isn't 'paid', surface a note (the poll
  // keeps running). In test lanes PaymentForm settles immediately, so this
  // is mostly a safety net for a stuck webhook.
  useEffect(() => {
    if (!paymentInitiated || quoteQuery.data?.status === 'paid') {
      setConfirmingSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setConfirmingSlow(true), 20000);
    return () => window.clearTimeout(timer);
  }, [paymentInitiated, quoteQuery.data?.status]);

  async function downloadOrderPdf(): Promise<void> {
    setDownloadMsg(null);
    try {
      const url = await quotesApi.getOrderDocumentUrl(id!);
      if (url) window.open(url, '_blank', 'noopener');
      else setDownloadMsg(t('page.download.notReady'));
    } catch {
      setDownloadMsg(t('page.download.fetchFailed'));
    }
  }

  // ADR-008 §3: payment surfaces are blocked during impersonation. We
  // hide the UI entirely (server enforces the block; this is the
  // customer-trust signal — sales is never standing in front of a
  // card form on a customer's behalf). Placed AFTER the hooks so hook
  // order stays stable across renders (rules of hooks).
  if (impersonating) {
    return (
      <div className="card">
        <h2>{t('page.impersonation.title')}</h2>
        <p>{t('page.impersonation.body')}</p>
        <p>
          <Link to={id ? `/quotes/${id}` : '/quotes'}>{t('page.actions.backToQuote')}</Link>
        </p>
      </div>
    );
  }

  if (!id) return <p>{t('page.missingQuoteId')}</p>;
  if (quoteQuery.isLoading) return <p>{t('common:states.loading')}</p>;
  if (quoteQuery.error || !quoteQuery.data) {
    return <div className="banner banner--error">{t('page.error.loadQuoteFailed')}</div>;
  }

  const quote = quoteQuery.data;

  if (quote.status === 'paid') {
    return (
      <div className="card">
        <div className="banner banner--success">
          <strong>{t('page.paid.title')}</strong>
        </div>
        <p>
          <Trans
            i18nKey="page.paid.reference"
            t={t}
            values={{ reference: quote.id }}
            components={[<strong key="reference" />]}
          />
        </p>
        <p>{t('page.paid.emailedConfirmation')}</p>
        <p>
          <button type="button" onClick={() => void downloadOrderPdf()}>
            {t('page.paid.downloadButton')}
          </button>
        </p>
        {downloadMsg && <p className="help">{downloadMsg}</p>}
        <p>
          <Link to="/quotes">{t('page.paid.backToQuotes')}</Link>
        </p>
      </div>
    );
  }

  if (!PAYABLE_QUOTE_STATUSES.has(quote.status)) {
    return (
      <div className="banner banner--warn">
        {t('page.notPayable.body', { status: quoteStatusLabel(quote.status) })}{' '}
        <Link to={`/quotes/${id}`}>{t('page.actions.backToQuote')}</Link>
      </div>
    );
  }

  const currency = quote.currency ?? 'USD';
  const hasTerms = Boolean(quote.conditions_text || quote.terms || quote.tax_disclosure);
  const totalAmount = formatCurrency(Number(quote.total_sale_amount), currency);

  return (
    <>
      <div className="card">
        <h2 style={{ marginTop: 0 }}>
          {t('page.header.title', {
            title: quote.project_title ?? t('page.header.untitledQuote'),
          })}
        </h2>
        <p style={{ marginTop: 0 }}>
          <Link to={`/quotes/${id}`}>{t('page.header.viewFullQuote')}</Link>
        </p>
        {(quote.sales_rep_name || quote.sales_rep_email) && (
          <p style={{ color: 'var(--color-text-muted)' }}>
            {t('page.contact.label')} {quote.sales_rep_name}
            {quote.sales_rep_email ? (
              <> · <a href={`mailto:${quote.sales_rep_email}`}>{quote.sales_rep_email}</a></>
            ) : null}
          </p>
        )}

        <h3>{t('page.summary.title')}</h3>
        <OrderLineItems quote={quote} />
        {quote.tax_disclosure && (
          <p className="help" style={{ marginTop: 'var(--space-2, 8px)' }}>{quote.tax_disclosure}</p>
        )}
        {quote.artwork_pending && (
          // "Quote now, art to follow" — the customer is about to authorize
          // payment, so surface the "subject to change" caveat here too. Nexus
          // owns the wording (mirrored in pricingCopy).
          <p className="help" style={{ marginTop: 'var(--space-2, 8px)', fontStyle: 'italic' }}>
            {ARTWORK_PENDING_DISCLAIMER}
          </p>
        )}

        {/* Charged total = subtotal + tax + shipping (ADR-010 §12). Tax +
            shipping are SEPARATE figures, never folded into the priced subtotal
            above. Shipping is a STUB today — the portal computes no rate; it's
            calculated by Argo/your carrier when the order is finalized. The
            amount authorized below is the priced subtotal only. The mode is
            the customer's stored pick, never the address rows' stamp, which
            also reads 'pickup' on a fulfilled order. */}
        {resolveDeliveryMode(quote.order_services) === 'shipping' && (
          <p className="help" style={{ marginTop: 'var(--space-2, 8px)' }}>
            <strong>{t('page.shipping.label')}</strong>{' '}
            {quote.order_services?.shipping_service === 'customer_labels'
              ? t('page.shipping.customerLabelsNote')
              : lookupShippingRate().reason}
            {quote.order_services?.shipping_service === 'customer_labels' &&
              quote.order_services.third_party_account && (
                <>
                  {' '}
                  {t('page.shipping.accountNote', {
                    account: quote.order_services.third_party_account,
                  })}
                </>
              )}
          </p>
        )}
        {resolveDeliveryMode(quote.order_services) === 'pickup' && (
          <p className="help" style={{ marginTop: 'var(--space-2, 8px)' }}>
            <strong>{t('page.delivery.label')}</strong> {t('page.delivery.pickupNote')}
          </p>
        )}

        {hasTerms && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer' }}>{t('page.terms.summary')}</summary>
            {quote.terms && <p className="help">{quote.terms}</p>}
            {quote.conditions_text && (
              <p className="help" style={{ whiteSpace: 'pre-wrap' }}>{quote.conditions_text}</p>
            )}
          </details>
        )}

        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 16 }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>
            <Trans
              i18nKey="page.terms.accept"
              t={t}
              context={hasTerms ? 'above' : undefined}
              values={{ amount: totalAmount }}
              components={[
                <strong key="amount" />,
                <a key="terms" href={TERMS_URL} target="_blank" rel="noopener noreferrer" />,
                <a key="privacy" href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" />,
              ]}
            />
          </span>
        </label>

        {errorMessage && (
          <div className="banner banner--error" style={{ marginTop: 16 }}>{errorMessage}</div>
        )}
      </div>

      {accepted && checkout && (
        <PaymentForm
          checkout={checkout}
          onPaymentInitiated={() => setPaymentInitiated(true)}
        />
      )}
      {accepted && !checkout && !errorMessage && <p>{t('page.preparingCheckout')}</p>}
      {!accepted && (
        <p className="help">{t('page.acceptToContinue')}</p>
      )}
      {confirmingSlow && (
        <div className="banner banner--warn">
          <strong>{t('page.confirming.title')}</strong> {t('page.confirming.body')}
        </div>
      )}
    </>
  );
}
